import { FC, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tApplicationRetailPath } from "src/redux/Application/types";
import { tReduxState } from "src/redux/types";
import { normalizeStateName } from "src/util/gFormatting";
import { aPriceRange, aRetailCategories, aRetailStoreCount, aStates, hearAboutUsOptions } from "../util/comboBoxData";
import {
  FormComboBoxInput,
  TextFormInput,
  tValidType,
  ValidatedComboBoxInput,
  ValidatedTextFormInput,
} from "./FormInputs";
import { YesNo } from "./YesNo";
import { NumberInput } from ".";
import { useIsMobile } from "src/hooks/ui";
import { PdStack } from "./layout";

const stateNormalizer = (val: string) => normalizeStateName(val) || "State";

const companyNameSelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "retailer" ? ApplicationReducer.name : "";

const address1Selector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.address1;

const addressTwoSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.address2;

const cityNameSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.city;

const stateSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.state_or_province;

const postalCodeSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.postal_code;

const taxIdSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.taxId;

const websiteSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.website;

const facebookSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.socialMedia?.facebook ?? "";

const instagramSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.socialMedia?.instagram ?? "";

const linkedInSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.socialMedia?.linkedin ?? "";

const eCommerceLinkSelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "retailer" ? ApplicationReducer.ecommerceLink : "";

const retailCategorySelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "retailer" ? ApplicationReducer.category : undefined;

const pricePointSelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "retailer" ? ApplicationReducer.price : undefined;

const locationQuantitySelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "retailer" ? ApplicationReducer.locationQuantity : undefined;

const noteSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.note;
const hearAboutUsSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.hearAboutUs;
const _isShown = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.isShown;

export const RetailApplication: FC<{
  isValid: tValidType;
  setValid: React.Dispatch<React.SetStateAction<tValidType>>;
}> = ({ isValid, setValid }) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const rAnnualSales = useSelector((state) =>
    state.ApplicationReducer.type === "retailer" ? state.ApplicationReducer.annualSales ?? 0 : 0,
  );
  const rEcommerceLink = useSelector(({ ApplicationReducer }) =>
    ApplicationReducer.type === "retailer" ? ApplicationReducer.ecommerceLink : "",
  );
  const [hasECommerceStore, setHasECommerceStore] = useState(!rEcommerceLink);

  const eCommerceTrue = () => {
    setHasECommerceStore(true);
  };

  const eCommerceFalse = () => {
    setHasECommerceStore(false);
    dispatch({ type: "UPDATE_RETAIL_APPLICATION", applicationPart: { ecommerceLink: "" } });
  };

  return (
    <>
      <View style={styles.gApplyTablePageContainer}>
        <View style={styles.gFormContainer}>
          <Text style={styles.sectionTitle}>Company Information</Text>
          <ValidatedTextFormInput
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            myTitle="Company Name *"
            uniqueName="companyName"
            rules={{ required: { message: "Required" } }}
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "Company Name",
              autoCompleteType: "organization",
            }}
            dispatchAction={{ type: "UPDATE_RETAIL_APPLICATION", applicationPart: { name: "" } }}
            dispatchPath="applicationPart.name"
            setController={setValid}
            selectorFunction={companyNameSelector}
            isShown={_isShown}
          />
          <ValidatedTextFormInput
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            rules={{ required: { message: "Required" } }}
            myTitle="Address *"
            uniqueName="address1"
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "1111 Street name",
              autoCompleteType: "address-line1",
            }}
            dispatchAction={{
              type: "UPDATE_RETAIL_APPLICATION",
              applicationPart: { address: { address1: "" } },
            }}
            dispatchPath="applicationPart.address.address1"
            setController={setValid}
            selectorFunction={address1Selector}
            isShown={_isShown}
          />
          <TextFormInput
            wrapperStyle={styles.gFormRowNoMarginSingle}
            titleStyle={styles.P08_01_InputHeader}
            myTitle="Address Line 2"
            textInputProps={{
              placeholder: "E.g. Apt. 1",
              autoCompleteType: "address-line2",
              style: styles.gLinkBankInput,
            }}
            dispatchAction={{
              type: "UPDATE_RETAIL_APPLICATION",
              applicationPart: { address: { address2: "" } },
            }}
            dispatchPath="applicationPart.address.address2"
            selectorFunction={addressTwoSelector}
          />

          <PdStack
            gap={7}
            direction={isMobile ? "vertical" : "horizontal"}
            style={[styles.zIndex10, isMobile ? {} : styles.alignEnd, styles.mb7]}
          >
            <View style={styles.flex1}>
              <ValidatedTextFormInput
                titleProps={{ style: styles.P08_01_InputHeader }}
                myTitle="City *"
                uniqueName="cityName"
                rules={{ required: { message: "Required" } }}
                controller={isValid}
                textInputProps={{
                  style: styles.gLinkBankTripleInput,
                  placeholder: "City Name",
                }}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { address: { city: "" } },
                }}
                dispatchPath="applicationPart.address.city"
                selectorFunction={cityNameSelector}
                setController={setValid}
                isShown={_isShown}
              />
            </View>
            <View style={[styles.zIndex10, styles.flex1]}>
              <ValidatedComboBoxInput
                wrapperStyle={styles.flex1}
                comboBoxStyle={styles.comboBoxReset}
                titleProps={{ style: styles.P08_01_InputHeader }}
                controller={isValid}
                ComboOptions={aStates}
                rules={{ required: { message: "Required" } }}
                uniqueName="State"
                myTitle="State *"
                placeholder="State"
                normalizerFunction={stateNormalizer}
                autoComplete="address-level1"
                isShown={_isShown}
                setController={setValid}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { address: { state_or_province: "" } },
                }}
                dispatchPath="applicationPart.address.state_or_province"
                selectorFunction={stateSelector}
              />
            </View>
            <View style={styles.flex1}>
              <ValidatedTextFormInput<tApplicationRetailPath>
                titleProps={{ style: styles.P08_01_InputHeader }}
                controller={isValid}
                uniqueName="ZipCode"
                myTitle="Zip Code *"
                rules={{
                  required: { message: "Required" },
                  pattern: { pattern: /\d{5}/, message: "Enter 5 digit zipcode" },
                }}
                textInputProps={{
                  placeholder: "12345",
                  style: styles.gLinkBankTripleInput,
                  maxLength: 5,
                  autoCompleteType: "postal-code",
                }}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { address: { postal_code: "" } },
                }}
                dispatchPath="applicationPart.address.postal_code"
                setController={setValid}
                isShown={_isShown}
                selectorFunction={postalCodeSelector}
              />
            </View>
          </PdStack>

          <PdStack
            gap={7}
            direction={isMobile ? "vertical" : "horizontal"}
            style={[isMobile ? {} : styles.alignEnd, styles.mb7]}
          >
            <View>
              <TextFormInput
                titleStyle={styles.P08_01_InputHeader}
                myTitle="Tax ID #"
                textInputProps={{
                  style: styles.gLinkBankInput,
                  placeholder: "Company Tax Id",
                }}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { taxId: "" },
                }}
                dispatchPath="applicationPart.taxId"
                selectorFunction={taxIdSelector}
              />
            </View>
            <View style={styles.flex1}>
              <ValidatedTextFormInput<tApplicationRetailPath>
                titleProps={{ style: styles.P08_01_InputHeader }}
                myTitle="Website *"
                uniqueName="website"
                rules={{ required: { message: "Required" } }}
                controller={isValid}
                textInputProps={{
                  style: styles.gLinkBankInput,
                  placeholder: "Company Website",
                  autoCompleteType: "url",
                }}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { website: "" },
                }}
                dispatchPath="applicationPart.website"
                setController={setValid}
                selectorFunction={websiteSelector}
                isShown={_isShown}
              />
            </View>
          </PdStack>

          <PdStack
            gap={7}
            direction={isMobile ? "vertical" : "horizontal"}
            style={[styles.zIndex10, isMobile ? {} : styles.alignEnd, styles.mb7]}
          >
            <View style={styles.flex1}>
              <ValidatedTextFormInput<tApplicationRetailPath>
                titleProps={{ style: styles.P08_01_InputHeader }}
                controller={isValid}
                myTitle="Facebook"
                uniqueName="facebook"
                textInputProps={{
                  placeholder: "www.facebook.com/storename",
                  style: styles.gLinkBankInput,
                }}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { socialMedia: { facebook: "" } },
                }}
                dispatchPath="applicationPart.socialMedia.facebook"
                setController={setValid}
                selectorFunction={facebookSelector}
                isShown={_isShown}
              />
            </View>
            <View style={styles.flex1}>
              <ValidatedTextFormInput<tApplicationRetailPath>
                titleProps={{ style: styles.P08_01_InputHeader }}
                myTitle="Instagram"
                uniqueName="instagram"
                controller={isValid}
                textInputProps={{
                  placeholder: "www.instagram.com/storename",
                  style: styles.gLinkBankInput,
                }}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { socialMedia: { instagram: "" } },
                }}
                dispatchPath="applicationPart.socialMedia.instagram"
                selectorFunction={instagramSelector}
                setController={setValid}
                isShown={_isShown}
              />
            </View>
            <View style={styles.flex1}>
              <ValidatedTextFormInput<tApplicationRetailPath>
                titleProps={{ style: styles.P08_01_InputHeader }}
                controller={isValid}
                myTitle="LinkedIn"
                uniqueName="linkedin"
                textInputProps={{
                  placeholder: "www.LinkedIn.com/storename",
                  style: styles.gLinkBankInput,
                }}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { socialMedia: { linkedin: "" } },
                }}
                dispatchPath="applicationPart.socialMedia.linkedin"
                setController={setValid}
                selectorFunction={linkedInSelector}
                isShown={_isShown}
              />
            </View>
          </PdStack>

          <View style={styles.gLinkBankFormItemYesNo}>
            <Text style={styles.P08_01_InputHeader}>Do you have an e-commerce store? *</Text>
            <View style={styles.gCheckMark2BoxContainer}>
              <YesNo
                myState={hasECommerceStore}
                myText={["No", "Yes"]}
                onPressTrue={eCommerceTrue}
                onPressFalse={eCommerceFalse}
              />
            </View>
          </View>

          {hasECommerceStore && (
            <ValidatedTextFormInput<tApplicationRetailPath>
              wrapperStyle={styles.gFormRowNoMarginSingle}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Please Provide a Website"
              uniqueName="usBased"
              controller={isValid}
              setController={setValid}
              isShown={_isShown}
              textInputProps={{
                placeholder: "Provide url to online store",
                style: styles.gLinkBankInput,
              }}
              dispatchAction={{
                type: "UPDATE_RETAIL_APPLICATION",
                applicationPart: { ecommerceLink: "" },
              }}
              dispatchPath="applicationPart.ecommerceLink"
              selectorFunction={eCommerceLinkSelector}
            />
          )}
        </View>
      </View>

      {/* Second View */}
      <View style={styles.gTablePageContainerBottom}>
        <View style={styles.gFormContainer}>
          <Text style={styles.sectionTitle}>About Your Business</Text>
          <View style={styles.gFormRowNoMarginZIndex}>
            <FormComboBoxInput<tApplicationRetailPath>
              wrapperStyle={[styles.gLinkBankFormItemZIndex, styles.zIndexFourteen]}
              titleStyle={styles.P08_01_InputHeader}
              ComboOptions={aRetailCategories}
              dispatchAction={{
                type: "UPDATE_RETAIL_APPLICATION",
                applicationPart: { category: undefined },
              }}
              dispatchPath="applicationPart.category"
              selectorFunction={retailCategorySelector}
              placeholder={"E.g. Gift Shop"}
              myTitle="What category best describes your store?"
            />
            <FormComboBoxInput<tApplicationRetailPath>
              wrapperStyle={[styles.gLinkBankFormItemZIndex, styles.zIndexTwelve]}
              titleStyle={styles.P08_01_InputHeader}
              ComboOptions={aPriceRange}
              dispatchAction={{
                type: "UPDATE_RETAIL_APPLICATION",
                applicationPart: { price: undefined },
              }}
              dispatchPath="applicationPart.price"
              selectorFunction={pricePointSelector}
              placeholder={"Please select a price point"}
              myTitle="Average retail price point of your Brands"
            />
            <FormComboBoxInput<tApplicationRetailPath>
              wrapperStyle={styles.gLinkBankFormItemNoMarginZIndex}
              titleStyle={styles.P08_01_InputHeader}
              ComboOptions={aRetailStoreCount}
              dispatchAction={{
                type: "UPDATE_RETAIL_APPLICATION",
                applicationPart: { locationQuantity: undefined },
              }}
              dispatchPath="applicationPart.locationQuantity"
              selectorFunction={locationQuantitySelector}
              placeholder={"Number Including E-Commerce Site"}
              myTitle="How many Retail Locations do you have?"
            />
          </View>
          <TextFormInput<tApplicationRetailPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleStyle={styles.P08_01_InputHeader}
            myTitle="More Information"
            textInputProps={{
              multiline: true,
              numberOfLines: 12,
              style: styles.gLinkBankInputBox,
              placeholder: "Tell us a little more about you and your business.",
            }}
            dispatchAction={{
              type: "UPDATE_RETAIL_APPLICATION",
              applicationPart: { note: "" },
            }}
            dispatchPath="applicationPart.note"
            selectorFunction={noteSelector}
          />

          <PdStack gap={7} direction={isMobile ? "vertical" : "horizontal"} style={isMobile ? {} : styles.alignEnd}>
            <View style={[styles.zIndex10, styles.flex1]}>
              <ValidatedComboBoxInput<tApplicationRetailPath>
                wrapperStyle={styles.flex1}
                comboBoxStyle={styles.comboBoxReset}
                titleProps={{ style: styles.P08_01_InputHeader }}
                controller={isValid}
                ComboOptions={hearAboutUsOptions}
                rules={{ required: { message: "Required" } }}
                uniqueName="HearAbout"
                myTitle="How did you hear about us? *"
                placeholder="Select how did you hear about us"
                isShown={_isShown}
                setController={setValid}
                dispatchAction={{
                  type: "UPDATE_RETAIL_APPLICATION",
                  applicationPart: { hearAboutUs: "social-media" },
                }}
                dispatchPath="applicationPart.hearAboutUs"
                selectorFunction={hearAboutUsSelector}
              />
            </View>
            <View>
              <Text style={styles.P08_01_InputHeader}>Annual Sales</Text>
              <NumberInput
                unit={{ label: "$", location: "start" }}
                style={styles.gFormInput}
                value={rAnnualSales.toString()}
                onChangeNumber={(newValue) =>
                  dispatch({ type: "UPDATE_RETAIL_APPLICATION", applicationPart: { annualSales: newValue } })
                }
              />
            </View>
          </PdStack>
        </View>
      </View>
    </>
  );
};

export default RetailApplication;

const styles = StyleSheet.create({
  P08_01_InputHeader: {
    ...kFonts.poppinsRegular,
    color: kColors.darkGray,
    letterSpacing: 1.1,
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  comboBoxReset: {
    marginBottom: 0,
  },
  flex1: {
    flex: 1,
  },
  gApplyTablePageContainer: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginTop: 7 * kStyleConst.kSpacing,
    zIndex: 2,
  },
  gCheckMark2BoxContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  gFormContainer: {
    marginBottom: 12 * kStyleConst.kSpacing,
    marginHorizontal: 13 * kStyleConst.kSpacing,
    marginTop: 12 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  gFormInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingRight: 40,
    paddingVertical: 10,
    width: "100%",
  },
  gFormRowNoMarginSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 7 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginValidatedSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginZIndex: {
    alignItems: "flex-end",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankFormItemNoMarginZIndex: {
    flex: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankFormItemYesNo: {
    flexDirection: "row",
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemZIndex: {
    flex: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 100000,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputBox: {
    borderColor: kColors.brandPink,
    borderRadius: 2,
    borderWidth: 1,
    flex: 1,
    height: kStyleConst.kApplicationNotesBoxHeight,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    paddingLeft: 2 * kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankTripleInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gTablePageContainerBottom: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginVertical: 7 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  mb7: {
    marginBottom: 7 * kStyleConst.kSpacing,
  },
  sectionTitle: {
    ...kFonts.ralewayLargeLight,
    alignSelf: "flex-start",
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  zIndex10: {
    zIndex: 10,
  },
  zIndexFourteen: {
    zIndex: 14,
  },
  zIndexTwelve: {
    zIndex: 12,
  },
});
